import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

// This directive targets the goab-button component's internal icon element to set an appropriate aria-label based on the button's label,
// ensuring that screen readers can accurately describe the button's purpose. It uses a retry mechanism to handle cases where the button's
// content may not be immediately available in the DOM.
// Usage: Add the buttonIconAccessibility directive to any goab-button element and provide a customAriaLabel input to enhance its accessibility
// by ensuring that the internal icon has a meaningful aria-label for screen readers.
@Directive({
  selector: '[buttonIconAccessibility]',
  standalone: true,
})
export class ButtonIconAccessibilityDirective
  implements AfterViewInit, OnChanges
{
  @Input() customAriaLabel = '';

  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    this.updateAriaLabel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customAriaLabel']) {
      this.updateAriaLabel();
    }
  }

  private updateAriaLabel(): void {
    setTimeout(() => {
      const host = this.el.nativeElement.querySelector(
        'goab-button > goa-button'
      )?.shadowRoot;

      if (!host) {
        return;
      }

      const iconHost = host.querySelector('button > goa-icon')?.shadowRoot;
      if (!iconHost) {
        return;
      }

      const iconElement = iconHost.querySelector('div[role="img"]');
      if (!iconElement) {
        return;
      }

      // The icon carries stray aria-expanded/aria-controls attributes that axe flags as
      // unsupported ARIA attributes; they're not relevant to a static icon.
      iconElement.removeAttribute('aria-expanded');
      iconElement.removeAttribute('aria-controls');

      if (!this.customAriaLabel?.trim()) {
        return;
      }

      iconElement.setAttribute('aria-label', this.customAriaLabel.trim());
    }, 0);
  }
}
